import { useCallback, useState } from "react";
import axios from "axios";
import { API_URL } from "../config";
import { getTotalAvailableStock } from "../pages/Dashboard/Pharmacy/utils/scanCart";

const CACHE_TTL_MS = 45000;
const scanCache = new Map();
const scanInflight = new Map();

const normalize = (value) => String(value || "").trim().toLowerCase();

const byExpiry = (left, right) =>
  String(left.expiry_date || "").localeCompare(String(right.expiry_date || ""));

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const readCache = (code) => {
  const hit = scanCache.get(code);
  if (!hit) return null;
  if (Date.now() - hit.at > CACHE_TTL_MS) {
    scanCache.delete(code);
    return null;
  }
  return hit.data;
};

const writeCache = (code, data) => {
  scanCache.set(code, { at: Date.now(), data });
};

export const findLocalStockByBarcode = (stockList = [], barcode) => {
  const code = normalize(barcode);
  if (!code) return null;
  const matches = asList(stockList).filter(
    (item) => normalize(item.barcode) === code,
  );
  if (!matches.length) return null;
  const today = new Date().toISOString().split("T")[0];
  const available = matches
    .filter(
      (item) =>
        Number(item.quantity) > 0 &&
        (!item.expiry_date || String(item.expiry_date) >= today),
    )
    .sort(byExpiry);
  return available[0] || null;
};

export const findLocalMedicineByBarcode = (medicineList = [], barcode) => {
  const code = normalize(barcode);
  if (!code) return null;
  return (
    asList(medicineList).find((item) => normalize(item.barcode) === code) ||
    null
  );
};

const useBarcodeLookup = (token) => {
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(
    async (barcode, { stockList, medicineList, complete = false } = {}) => {
      const code = String(barcode || "").trim();
      if (!code) {
        return { found: false, barcode: code, status: "empty" };
      }
      const cacheKey = normalize(code);

      if (!complete) {
        const localStock = findLocalStockByBarcode(stockList, code);
        if (localStock) {
          const totalQty = getTotalAvailableStock(stockList, code, localStock);
          const stockPayload = {
            ...localStock,
            quantity: totalQty,
            available_stock: totalQty,
          };
          return {
            found: true,
            barcode: code,
            medicine: {
              id: localStock.medicine,
              name: localStock.medicine_display,
              category_display: localStock.category_display,
              barcode: localStock.barcode,
            },
            best_stock: stockPayload,
            stock: [stockPayload],
            total_quantity: totalQty,
            status:
              totalQty > 0
                ? totalQty < 11
                  ? "low_stock"
                  : "in_stock"
                : "out_of_stock",
            source: "local-stock",
          };
        }

        const localMedicine = findLocalMedicineByBarcode(medicineList, code);
        if (localMedicine) {
          return {
            found: true,
            barcode: code,
            medicine: localMedicine,
            best_stock: null,
            stock: [],
            total_quantity: 0,
            status: "out_of_stock",
            source: "local-medicine",
          };
        }
      }

      const cached = readCache(cacheKey);
      if (cached) {
        return { ...cached, barcode: cached.barcode || code, source: "cache" };
      }

      if (scanInflight.has(cacheKey)) {
        return scanInflight.get(cacheKey);
      }

      const request = (async () => {
        setLoading(true);
        try {
          const headers = token ? { Authorization: `Token ${token}` } : {};
          const response = await axios.get(`${API_URL}/medicines/scan/`, {
            headers,
            params: { barcode: code },
            timeout: 8000,
          });
          const payload = response.data || {};
          const result = {
            ...payload,
            found: Boolean(payload.found),
            barcode: payload.barcode || code,
            source: "api",
          };
          if (result.found) {
            writeCache(cacheKey, result);
          }
          return result;
        } catch (error) {
          return {
            found: false,
            barcode: code,
            status: "error",
            error: error?.response?.data?.detail || error.message,
          };
        } finally {
          scanInflight.delete(cacheKey);
          setLoading(false);
        }
      })();

      scanInflight.set(cacheKey, request);
      return request;
    },
    [token],
  );

  return { lookup, loading };
};

export default useBarcodeLookup;
